import { useNavigate } from 'react-router-dom';
import { Home, TrendingUp, Compass } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { trackEvent } from '@/lib/analytics';

const paths = [
  {
    icon: Home,
    title: 'Make it your home',
    description: 'A luxury address in Grand Harbour—designed for connection, calm, and everyday ease.',
    color: 'text-primary',
    action: 'live',
  },
  {
    icon: Compass,
    title: 'Experience the lifestyle',
    description: 'From lagoon to plaza—wellness, dining, and community, woven into daily life.',
    color: 'text-primary',
    action: 'amenities',
  },
  {
    icon: TrendingUp,
    title: 'Explore the opportunity',
    description: 'A waterfront lifestyle with enduring demand—explore the opportunity in a proven location.',
    color: 'text-accent',
    action: 'invest',
  },
];

export const BuyerPaths = () => {
  const navigate = useNavigate();

  const handlePathClick = (pathTitle: string, action: string) => {
    trackEvent('cta_click', { location: 'buyer_paths', path: pathTitle });

    switch (action) {
      case 'live':
        navigate('/residences');
        break;
      case 'invest':
        navigate('/invest');
        break;
      case 'amenities':
        navigate('/amenities');
        break;
      default:
        break;
    }
  };

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden">
      {/* Ghosted lifestyle background — family playing on the beach (Unsplash, royalty-free) */}
      <img
        src="/img/beach.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.28] object-center"
        loading="lazy"
      />
      {/* Existing background color layer */}
      <div className="absolute inset-0 bg-secondary/30" />

      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold text-foreground mb-6">
            Begin Your Grand Harbour Story
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Choose your next step—make it your home, experience the lifestyle, or explore the opportunity at The Pearl.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {paths.map((path) => {
            const Icon = path.icon;
            return (
              <Card
                key={path.title}
                className="hover-lift cursor-pointer border-none shadow-soft group transition-all duration-300 hover:shadow-xl hover:-translate-y-2"
                onClick={() => handlePathClick(path.title, path.action)}
              >
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-6 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <Icon className={`w-8 h-8 ${path.color} group-hover:scale-110 transition-transform duration-300`} />
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground mb-3">
                    {path.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {path.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
